import { Button, Progress } from 'antd';
import Task from '../../../types/Task';
import colorTokens from '../../../constants/design_system/colors';
import { defaultTaskStatuses, TaskStatusGroup } from '../../../types/TaskStatus';
import { TaskStatus } from '../../../types/TaskStatus';
import { useGetWorkspace } from '../../../serverState/workspace';
import { User } from '../../../types/User';
import { CheckCircleFilled, DeleteOutlined, MoreOutlined, PlusOutlined } from '@ant-design/icons';
import TaskTable from '../../../components/TaskTable/TaskTable';
import CustomTooltip from '../../../components/UI/Tooltip/CustomTooltip';
import { Column } from '../../../types/WorkspaceViewColumn';
import { TaskType } from '../../../types/TaskType';
import { sortGroupTasks } from '../../../components/TaskManagement/helpers/sortHelpers';
import { WorkspaceView } from '../../../types/WorkspaceView';
import BaseDropdown from '../../../components/UI/Dropdown/BaseDropdown';

interface TaskSubtasksProps {
  task: Task;
  subtasks: Task[];
  setSubtasks: (subtasks: Task[]) => void;
  usersWithAccessToSpace: User[];
  onOpenSubtask: (subtask: Task) => void;
  onAddSubtask: () => void;
  onDeleteAllSubtasks: () => void;
  view?: WorkspaceView;
}

export const TaskSubtasks: React.FC<TaskSubtasksProps> = ({
  task,
  subtasks,
  setSubtasks,
  usersWithAccessToSpace,
  onOpenSubtask,
  onAddSubtask,
  onDeleteAllSubtasks,
  view,
}) => {
  const workspaceQuery = useGetWorkspace();
  const taskStatuses: Record<TaskStatus['id'], TaskStatus> = (
    (workspaceQuery.data?.TaskStatus as TaskStatus[]) || defaultTaskStatuses
  ).reduce((prev, curr) => ({ ...prev, [curr.id]: curr }), {});
  const completedSubtasks = subtasks.filter(
    (subtask) => taskStatuses[subtask.statusId]?.group === TaskStatusGroup.completed
  );
  const taskOrder = workspaceQuery.data?.TaskOrder[0];
  const tasksOrderMap = new Map<number, number>(taskOrder?.taskIds?.map((id: number, index: number) => [id, index]));

  const optionsMenuItems = [
    {
      key: 'delete',
      label: <div className="justify-start text-red-600 text-sm font-normal">Delete All</div>,
      icon: <DeleteOutlined className="text-red-600" />,
    },
  ];

  if (subtasks.length === 0) {
    return null;
  }

  const sortedSubtasks = sortGroupTasks(
    subtasks.map((subtask) => ({
      key: subtask.id,
      ...subtask,
    })),
    tasksOrderMap,
    undefined,
    undefined,
    undefined,
    undefined,
    undefined,
    undefined
  );

  return (
    <div className="flex flex-col gap-2 mt-6 task-subtasks-section">
      <div className="h-6 flex items-center gap-1 text-brand-grey-dark text-xs font-semibold ml-[-28px]">
        <div className="relative w-6 h-4 flex items-center justify-center right-0 top-0">
          <BaseDropdown
            menu={{
              items: optionsMenuItems,
              onClick: (e) => {
                e.domEvent.stopPropagation();
                if (e.key === 'delete') {
                  onDeleteAllSubtasks();
                }
              },
            }}
            trigger={['click']}
            placement="bottomLeft"
            overlayStyle={{ width: 240 }}
          >
            <Button
              type="link"
              className="h-6 w-6 p-0 bg-white rounded shadow border border-neutral-300 hover:border-brand-taskonio-green justify-center items-center gap-1 inline-flex task-subtasks-section__options-menu"
            >
              <MoreOutlined className="text-neutral-900" />
            </Button>
          </BaseDropdown>
        </div>
        {task.type === TaskType.epic ? 'Items' : 'Subitems'}
        <div className="w-4 h-4 relative ml-1">
          {completedSubtasks.length < subtasks.length && (
            <Progress
              type="circle"
              percent={subtasks.length ? Math.round((completedSubtasks.length / subtasks.length) * 100) : 0}
              size={16}
              strokeWidth={10}
              showInfo={false}
              strokeColor={colorTokens.brand['taskonio-green']}
              trailColor={colorTokens.neutral['600']}
            />
          )}
          {completedSubtasks.length === subtasks.length && (
            <div className="w-4 h-4 flex items-center justify-center text-neutral-900 text-xs font-semibold p-0.5">
              <CheckCircleFilled style={{ fontSize: '16px' }} className="text-taskonioGreen-600" />
            </div>
          )}
        </div>
        <div className="flex items-center justify-center text-neutral-900 text-xs font-semibold p-0.5">
          {completedSubtasks.length}/{subtasks.length}
        </div>
        {task.stage !== 'triage' && (
          <div className="task-subtasks-section__add-subtask">
            <CustomTooltip title="Add Subtask">
              <Button
                type="link"
                className="h-6 ml-1 p-0 bg-white rounded shadow border border-neutral-300 hover:border-brand-taskonio-green justify-start items-center gap-1 inline-flex"
                onClick={onAddSubtask}
              >
                <PlusOutlined className="text-neutral-900 hover:text-brand-taskonio-green p-1" />
              </Button>
            </CustomTooltip>
          </div>
        )}
      </div>
      <div className="flex flex-col gap-2 overflow-x-auto pb-2.5 mx-[-40px]">
        <TaskTable
          tableDataSource={sortedSubtasks}
          setTableDataSource={(newDataSource) => {
            const newSubtasks = newDataSource.map((item: any) => {
              const { key, ...rest } = item;
              return rest;
            });
            setSubtasks(newSubtasks);
          }}
          tableDataLoaded={false}
          usersWithAccessToSpace={usersWithAccessToSpace.map((user) => ({
            User: user,
            userId: user.id,
            workspaceId: workspaceQuery.data?.id,
          }))}
          showHeader={false}
          showMultiselect={false}
          selectedItems={[]}
          setSelectedItems={() => {}}
          handleEditEstimationSettings={() => {}}
          isPlanningTable={false}
          isSubtasksTable={true}
          onOpenSubtask={onOpenSubtask}
          filterColumns={[Column.epic, Column.deadline, Column.startDate, Column.label, Column.subitems]}
        />
      </div>
    </div>
  );
};
